"use client";

import { c as _c } from "react/compiler-runtime";
import { Delta } from "../delta-display";
import { formatDate } from "@/lib/format-data";
import { BODY_SEGMENTS, BODY_SEGMENT_LABELS } from "@/lib/measurement-body";
import { formatMeasurementValue, getSeverityBarClass, getSeverityColor } from "@/lib/measurement-calculations";
import { cx } from "@/lib/tailwind-utils";
import type { BodyMeasure, BodySegment, MeasurementField, MeasurementValueSummary, ReferenceRange } from "@/types/measurement";
export interface SegmentMeasureCell {
  value: MeasurementValueSummary;
  field: MeasurementField;
  delta: number | null;
  /** Bucket key of the scan `delta` is measured against. */
  comparedTo: string | null;
}
export type SegmentCells = Partial<Record<BodyMeasure, SegmentMeasureCell>>;
type Side = "lean" | "fat";

/**
 * Bars read like InBody's printout: length is the segment's % of normal, not
 * its mass. Mass only ever said the trunk outweighs an arm, which is true of
 * everyone. Each measure keeps one scale across all five segments so bar
 * lengths compare; fat runs far wider than lean (an ordinary scan prints a
 * 245% trunk), so it gets the longer one.
 */
const SIDES: Record<Side, {
  label: string;
  heading: string;
  mass: BodyMeasure;
  pct: BodyMeasure;
  scaleMax: number;
}> = {
  lean: {
    label: "Lean",
    heading: "Lean mass",
    mass: "LEAN_MASS",
    pct: "LEAN_PCT_OF_NORMAL",
    scaleMax: 200
  },
  fat: {
    label: "Fat",
    heading: "Fat mass",
    mass: "FAT_MASS",
    pct: "FAT_PCT_OF_NORMAL",
    scaleMax: 300
  }
};

// Segment name, then lean, then fat, once the card is wide enough for both
// measures on one line; below that each segment stacks.
const ROW_COLUMNS = "@[36rem]:grid-cols-[6.5rem_minmax(0,1fr)_minmax(0,1fr)] @[36rem]:items-center";

/**
 * The range a reading of exactly 100% falls in, which InBody calls normal.
 * Half-open like evaluateReferenceRange, so the marks bound the same band that
 * colours the bar.
 */
function normalBand(field: MeasurementField): ReferenceRange | null {
  return field.referenceRanges?.find(range => range.min != null && range.max != null && range.min <= 100 && 100 < range.max) ?? null;
}
function positionOnScale(pct: number, scaleMax: number): string {
  return `${Math.min(Math.max(pct / scaleMax, 0), 1) * 100}%`;
}
function RangeBar(t0) {
  const $ = _c(16);
  const {
    cell,
    measure,
    scaleMax
  } = t0;
  const pct = cell.value.numericValue;
  if (pct == null) {
    return null;
  }
  let t1;
  if ($[0] !== cell.field || $[1] !== cell.value.outcome?.severity || $[2] !== measure || $[3] !== pct || $[4] !== scaleMax) {
    const band = normalBand(cell.field);
    let t2;
    if ($[6] !== cell.value.outcome?.severity) {
      t2 = cx("h-full rounded-full", getSeverityBarClass(cell.value.outcome?.severity ?? null));
      $[6] = cell.value.outcome?.severity;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== pct || $[9] !== scaleMax) {
      t3 = positionOnScale(pct, scaleMax);
      $[8] = pct;
      $[9] = scaleMax;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3) {
      t4 = {
        width: t3
      };
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t2 || $[14] !== t4) {
      t5 = <div className={t2} style={t4} />;
      $[13] = t2;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t1 = <div aria-hidden="true" data-testid={`body-range-${measure}`} className="relative h-2 rounded-full bg-foreground-muted/20">{t5}{band && [band.min, band.max].map(edge => <div key={edge} className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full bg-foreground-muted" style={{
        left: positionOnScale(edge ?? 0, scaleMax)
      }} />)}</div>;
    $[0] = cell.field;
    $[1] = cell.value.outcome?.severity;
    $[2] = measure;
    $[3] = pct;
    $[4] = scaleMax;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function MeasureCell(t0) {
  const $ = _c(30);
  const {
    cells,
    side,
    previousScanKey
  } = t0;
  const {
    label,
    mass,
    pct,
    scaleMax
  } = SIDES[side];
  const massCell = cells[mass];
  const pctCell = cells[pct];
  const severity = pctCell?.value.outcome?.severity ?? null;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="w-7 shrink-0 text-tiny text-foreground-muted @[36rem]:hidden">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== massCell) {
    t2 = massCell ? formatMeasurementValue(massCell.value, massCell.field) : "\u2014";
    $[2] = massCell;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-body-strong tabular-nums text-foreground">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== massCell || $[7] !== previousScanKey) {
    t4 = massCell && massCell.delta != null && <Delta change={massCell.delta} field={massCell.field} size="xs" className="flex flex-wrap items-center gap-0.5 tabular-nums"><Delta.Icon /><Delta.Value />{massCell.comparedTo && massCell.comparedTo !== previousScanKey && <Delta.Since date={massCell.comparedTo} className="ml-0.5" />}</Delta>;
    $[6] = massCell;
    $[7] = previousScanKey;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">{t1}{t3}{t4}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== severity) {
    t6 = cx("shrink-0 text-caption tabular-nums", severity ? getSeverityColor(severity) : "text-foreground-muted");
    $[13] = severity;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== pctCell) {
    t7 = pctCell ? formatMeasurementValue(pctCell.value, pctCell.field) : "\u2014";
    $[15] = pctCell;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t5 || $[21] !== t8) {
    t9 = <div className="flex items-baseline justify-between gap-3">{t5}{t8}</div>;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== pct || $[24] !== pctCell || $[25] !== scaleMax) {
    t10 = pctCell && <RangeBar cell={pctCell} measure={pct} scaleMax={scaleMax} />;
    $[23] = pct;
    $[24] = pctCell;
    $[25] = scaleMax;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <div role="cell" className="min-w-0 space-y-1.5">{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
interface BodyBarsProps {
  cellsBySegment: Record<BodySegment, SegmentCells>;
  hoveredSegment: BodySegment | null;
  onSegmentHover: (segment: BodySegment, hovered: boolean) => void;
  /** Bucket key of the scan the changes are measured against. */
  previousScanKey: string | null;
}
export default function BodyBars(t0) {
  const $ = _c(17);
  const {
    cellsBySegment,
    hoveredSegment,
    onSegmentHover,
    previousScanKey
  } = t0;
  let t1;
  if ($[0] !== previousScanKey) {
    t1 = previousScanKey && <p className="px-2 text-caption text-foreground-muted">Change since {formatDate(previousScanKey)}</p>;
    $[0] = previousScanKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx("hidden gap-x-6 px-2 pb-1 text-tiny text-foreground-muted @[36rem]:grid", ROW_COLUMNS);
    t3 = <div role="columnheader">Segment</div>;
    t4 = ["lean", "fat"];
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div role="row" className={t2}>{t3}{(t4 as const).map(_temp)}</div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== cellsBySegment || $[7] !== hoveredSegment || $[8] !== onSegmentHover || $[9] !== previousScanKey) {
    t6 = BODY_SEGMENTS.map(segment => <div key={segment} role="row" tabIndex={0} data-testid={`body-bar-row-${segment}`} onMouseEnter={() => onSegmentHover(segment, true)} onMouseLeave={() => onSegmentHover(segment, false)} onFocus={() => onSegmentHover(segment, true)} onBlur={() => onSegmentHover(segment, false)} className={cx("grid grid-cols-1 gap-x-6 gap-y-2 rounded-lg px-2 py-2.5 outline-none transition-colors", ROW_COLUMNS, hoveredSegment === segment ? "bg-background-tertiary" : "hover:bg-background-tertiary")}><div role="cell" className="text-caption-strong text-foreground">{BODY_SEGMENT_LABELS[segment]}</div><MeasureCell cells={cellsBySegment[segment]} side="lean" previousScanKey={previousScanKey} /><MeasureCell cells={cellsBySegment[segment]} side="fat" previousScanKey={previousScanKey} /></div>);
    $[6] = cellsBySegment;
    $[7] = hoveredSegment;
    $[8] = onSegmentHover;
    $[9] = previousScanKey;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <div role="table" aria-label="Lean and fat by body segment">{t5}{t6}</div>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="px-2 text-caption text-foreground-muted">Bars show each segment's % of normal; the two marks are the edges of its normal range.</p>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t1 || $[15] !== t7) {
    t9 = <div className="min-w-0 space-y-2 @container">{t1}{t7}{t8}</div>;
    $[14] = t1;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function _temp(side) {
  return <div key={side} role="columnheader" className="flex justify-between gap-3"><span>{SIDES[side].heading}</span><span>% of normal</span></div>;
}